import { useCallback, useState } from 'react';
import type { FileNode } from '../protocol';
import { PAID_FEATURE_HINT, isPaidTier } from '../planTier';
import type { PlanTier } from '../protocol';
import {
  DEFAULT_SYNC_JOB_TYPE,
  loadSyncProfiles,
  newProfileId,
  saveSyncProfiles,
  syncTypeLabel,
  type SyncJobType,
  type SyncProfile,
} from '../sync/syncSettings';
import { pickLocalDirectory, saveDirHandle } from '../sync/localDirStore';
import type { SyncMode } from '../sync/syncEngine';
import { SyncDestPicker } from './SyncDestPicker';

export interface SyncDestination {
  pcId: string;
  ownerUserId: string;
  pcName: string;
  hostSharePath: string;
  hostShareName: string;
  defaultHostRelDir?: string;
}

export interface SyncSetupModalProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  destination: SyncDestination;
  planTier: PlanTier | null;
  shareRoot: FileNode;
  listHostDir: (relPath: string) => Promise<FileNode>;
  onRunJob: (profile: SyncProfile, mode: SyncMode) => Promise<void>;
  onProfilesChanged?: () => void;
}

export function SyncSetupModal({
  open,
  onClose,
  userId,
  destination,
  planTier,
  shareRoot,
  listHostDir,
  onRunJob,
  onProfilesChanged,
}: SyncSetupModalProps): JSX.Element | null {
  const paid = isPaidTier(planTier);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [localDir, setLocalDir] = useState<FileSystemDirectoryHandle | null>(null);
  const [hostRelDir, setHostRelDir] = useState<string | null>(null);
  const [setupSyncType, setSetupSyncType] = useState<SyncJobType>(DEFAULT_SYNC_JOB_TYPE);

  const reset = useCallback(() => {
    setLocalDir(null);
    setHostRelDir(null);
    setSetupSyncType(DEFAULT_SYNC_JOB_TYPE);
    setStatus(null);
  }, []);

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const pickLocal = async () => {
    if (!paid) {
      setStatus(PAID_FEATURE_HINT);
      return;
    }
    try {
      const dir = await pickLocalDirectory();
      if (!dir) return;
      setLocalDir(dir);
      setHostRelDir(null);
      setStatus(null);
    } catch (e) {
      setStatus(String(e));
    }
  };

  const confirmSetup = async () => {
    if (!localDir || hostRelDir === null) return;
    const profile: SyncProfile = {
      id: newProfileId(),
      label: localDir.name,
      localDirName: localDir.name,
      syncType: setupSyncType,
      pcId: destination.pcId,
      ownerUserId: destination.ownerUserId,
      pcName: destination.pcName,
      hostSharePath: destination.hostSharePath,
      hostShareName: destination.hostShareName,
      hostRelDir,
    };
    try {
      await saveDirHandle(profile.id, localDir);
      const existing = loadSyncProfiles(userId);
      saveSyncProfiles(userId, [...existing, profile]);
      onProfilesChanged?.();
      setBusy(true);
      setStatus(
        setupSyncType === 'two-way'
          ? 'First sync: merging with host…'
          : `First sync: uploading ${localDir.name}…`
      );
      await onRunJob(profile, 'initial');
      setStatus(`Setup complete (${syncTypeLabel(profile.syncType)})`);
      reset();
      onClose();
    } catch (e) {
      setStatus(String(e));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const canCreate = localDir && hostRelDir !== null && !busy;

  return (
    <div className="modal-backdrop" role="presentation" onClick={handleClose}>
      <div
        className="modal panel sync-setup-modal"
        role="dialog"
        aria-labelledby="sync-setup-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="sync-setup-title">Set up sync</h3>
        {!paid && <p className="warn">{PAID_FEATURE_HINT}</p>}
        <div className="sync-setup-step">
          <strong>1. Folder on this device</strong>
          {localDir ? (
            <p>{localDir.name}</p>
          ) : (
            <button type="button" className="btn" disabled={busy} onClick={() => void pickLocal()}>
              Choose local folder…
            </button>
          )}
        </div>
        {localDir && (
          <>
            <div className="sync-setup-step">
              <strong>2. Destination on {destination.hostShareName}</strong>
              <SyncDestPicker
                root={shareRoot}
                listHostDir={listHostDir}
                selectedRel={hostRelDir}
                onSelectRel={setHostRelDir}
                initialRel={destination.defaultHostRelDir}
              />
            </div>
            <fieldset className="sync-type-fieldset">
              <legend>3. Sync type</legend>
              <label className="sync-type-option">
                <input
                  type="radio"
                  name="sync-type-modal"
                  checked={setupSyncType === 'upload-only'}
                  onChange={() => setSetupSyncType('upload-only')}
                />
                Upload only <span className="sync-recommended">(recommended)</span>
              </label>
              <label className="sync-type-option">
                <input
                  type="radio"
                  name="sync-type-modal"
                  checked={setupSyncType === 'two-way'}
                  onChange={() => setSetupSyncType('two-way')}
                />
                Two-way sync
              </label>
            </fieldset>
          </>
        )}
        <div className="sync-setup-actions">
          <button type="button" className="btn" disabled={!canCreate} onClick={() => void confirmSetup()}>
            Create and run first sync
          </button>
          <button type="button" className="btn ghost" disabled={busy} onClick={handleClose}>
            Cancel
          </button>
        </div>
        {status && <p className="sync-status">{status}</p>}
      </div>
    </div>
  );
}
